import { Flex, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import Accordion from 'components/Accordion';
import TasksTable from '../components/TasksTable';

import { TaskManagementContext } from 'screens/task-management';
import useNotifyError from 'hooks/useNotifyError';
import useTasksQuery from 'screens/task-management/hooks/queries/useTasksQuery';
import useTaskManagementFilters from 'screens/task-management/hooks/useTaskManagementFilters';

import { convertTaskFiltersToApiParams } from 'screens/task-management/util';

import type { Task } from '../types';

const TaskViewContent = () => {
  const { t } = useTranslation();
  const { handleTaskRowClick } = useOutletContext<TaskManagementContext>();

  const { filters } = useTaskManagementFilters();
  const queryParams = useMemo(
    () => convertTaskFiltersToApiParams(filters),
    [filters],
  );

  const { data, isLoading, error } = useTasksQuery(queryParams);

  useNotifyError({
    error,
    msg: t('common.somethingWrong'),
  });

  const getRowClassNameForWeeklyTasks = (record: Task, index: number) => {
    let className = '';

    const isLastRow = index === (data?.weeklyTask?.length || 0) - 1;

    if (isLastRow) className += 'mb-3 ';

    // Grey out failed tasks in weekly tasks - because failed tasks in
    // weekly tasks already have a failure note attached to them
    if (record?.status === 'INCOMPLETE')
      className += `table-highlight table-highlight__grey`;

    return className;
  };

  const config = useMemo(
    () => [
      {
        title: t('taskManagement.taskView.tasksNeedingAttention'),
        headerContent: t('taskManagement.taskView.tasksNeedingAttentionDesc'),
        iconPrefixContent: data?.priorityTask?.length || 0,
        children: (
          <TasksTable
            onRowClick={handleTaskRowClick}
            data={data?.priorityTask}
            loading={isLoading}
          />
        ),
      },
      {
        title: t('taskManagement.taskView.weekTasks'),
        headerContent: t('taskManagement.taskView.weekTasksDesc'),
        iconPrefixContent: data?.weeklyTask?.length || 0,
        children: (
          <TasksTable
            onRowClick={handleTaskRowClick}
            data={data?.weeklyTask}
            loading={isLoading}
            rowClassName={getRowClassNameForWeeklyTasks}
          />
        ),
      },
      // TODO: Uncomment when backend supports this
      // {
      //   title: t('taskManagement.taskView.rescheduledTasks'),
      //   headerContent: t('taskManagement.taskView.rescheduledTasksDesc'),
      //   iconPrefixContent: 2,
      //   children: <TasksTable data={tasks} loading={isLoading} />,
      // },
    ],
    [t, data, isLoading, error],
  );

  return (
    <Flex vertical gap={24}>
      {config.map((item, index) => (
        <Accordion
          key={index}
          className="accordion-card"
          title={
            <Typography.Title className="m-0" level={5}>
              {item.title}
            </Typography.Title>
          }
          itemKey={String(index)}
          headerContent={item.headerContent}
          iconPrefixContent={
            <Typography.Text>{item.iconPrefixContent}</Typography.Text>
          }
          defaultActiveKey={String(index)}
          style={{ background: 'transparent' }}
        >
          <Flex className="mt-3">{item.children}</Flex>
        </Accordion>
      ))}
    </Flex>
  );
};

export default TaskViewContent;
